import React, { useContext, useState } from 'react';
import { Button, message, Modal } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import PageScaffold from '../../components/PageScaffold';
import { generateApiKey, revokeApiKey } from '../../../domain/use_cases/users/manageApiKey';
import CompanyPageHeader from './CompanyPageHeader';
import { getCompanyTabPath } from './useCompanyTabs';
import '../settings/SettingsPage.css';

export const COMPANY_SETTINGS_PAGE_PATH = getCompanyTabPath('settings');
export const COMPANY_SETTINGS_PAGE_TITLE = 'Paramètres';

const CompanySettingsPage: React.FC = () => {
  const { userDoc } = useContext(AuthenticationContext);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);

  const handleGenerateApiKey = async () => {
    setApiKeyLoading(true);
    const result = await generateApiKey();
    if (result.success && result.apiKey) {
      setApiKey(result.apiKey);
      setApiKeyRevealed(true);
      message.success('Clé API générée avec succès. Copiez-la maintenant, elle ne sera plus affichée.');
    } else {
      message.error(result.error || 'Erreur lors de la génération de la clé API');
    }
    setApiKeyLoading(false);
  };

  const handleRevokeApiKey = async () => {
    setApiKeyLoading(true);
    const result = await revokeApiKey();
    if (result.success) {
      setApiKey(null);
      setApiKeyRevealed(false);
      setRevokeModalVisible(false);
      message.success('Clé API révoquée avec succès.');
    } else {
      message.error(result.error || 'Erreur lors de la révocation de la clé API');
    }
    setApiKeyLoading(false);
  };

  const handleCopyApiKey = () => {
    const key = apiKey || userDoc?.apiKey;
    if (key) {
      navigator.clipboard.writeText(key);
      message.success('Clé API copiée dans le presse-papier');
    }
  };

  return (
    <PageScaffold
      header={<CompanyPageHeader />}
      title={COMPANY_SETTINGS_PAGE_TITLE}
      subtitle="Gérez votre clé d'accès à l'API REST pour vos intégrations."
    >
      <div className="settings-page__form">
        <div style={{
          background: '#f6f8fa',
          border: '1px solid #d0d7de',
          borderRadius: 8,
          padding: '20px 24px',
          marginBottom: 16,
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>Clé API</h3>
          <p style={{ margin: '0 0 16px', color: '#656d76', fontSize: 14 }}>
            Cette clé vous permet d'accéder à vos contacts, appels, SMS, messages vocaux et tâches via notre API.
          </p>

          {!apiKey && !userDoc?.apiKey && (
            <Button
              type="primary"
              icon={<ApiOutlined />}
              loading={apiKeyLoading}
              onClick={handleGenerateApiKey}
            >
              Générer une clé API
            </Button>
          )}

          {!apiKey && userDoc?.apiKey && (
            <div>
              <div style={{
                background: '#fff',
                border: '1px solid #d0d7de',
                borderRadius: 6,
                padding: '12px 14px',
                marginBottom: 12,
                fontFamily: 'monospace',
                fontSize: 13,
                color: '#1F2328',
              }}>
                {apiKeyRevealed
                  ? userDoc.apiKey
                  : (userDoc.apiKey?.length ?? 0) > 8
                    ? `${userDoc.apiKey!.slice(0, 4)}${'●'.repeat(Math.max(0, userDoc.apiKey!.length - 8))}${userDoc.apiKey!.slice(-4)}`
                    : '●'.repeat(userDoc.apiKey?.length ?? 32)}
              </div>
              <Button
                type="default"
                onClick={() => setApiKeyRevealed(!apiKeyRevealed)}
                style={{ marginRight: 8, width: 100 }}
              >
                {apiKeyRevealed ? 'Masquer' : 'Afficher'}
              </Button>
              <Button
                type="default"
                onClick={handleCopyApiKey}
                style={{ marginRight: 8 }}
              >
                Copier
              </Button>
              <Button
                type="primary"
                danger
                loading={apiKeyLoading}
                onClick={() => setRevokeModalVisible(true)}
              >
                Révoquer la clé
              </Button>
            </div>
          )}

          {apiKey && apiKeyRevealed && (
            <div>
              <div style={{
                background: '#fff',
                border: '2px solid #0969da',
                borderRadius: 6,
                padding: '12px 14px',
                marginBottom: 12,
                fontFamily: 'monospace',
                fontSize: 13,
                wordBreak: 'break-all',
                color: '#1F2328',
              }}>
                {apiKey}
              </div>
              <Button
                type="default"
                onClick={handleCopyApiKey}
                style={{ marginRight: 8 }}
              >
                Copier
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => setRevokeModalVisible(true)}
              >
                Révoquer
              </Button>
            </div>
          )}
        </div>

        <div style={{
          background: '#f6f8fa',
          border: '1px solid #d0d7de',
          borderRadius: 8,
          padding: '20px 24px',
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>Documentation</h3>
          <p style={{ margin: '0 0 12px', color: '#656d76', fontSize: 14 }}>
            Consultez la documentation interactive de l'API.
          </p>
          <Button
            type="default"
            icon={<ApiOutlined />}
            href="https://api.leedflow.com/api/external/v1/swagger"
            target="_blank"
          >
            Ouvrir
          </Button>
        </div>
      </div>

      <Modal
        title="Révoquer la clé API"
        open={revokeModalVisible}
        onCancel={() => setRevokeModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setRevokeModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={apiKeyLoading}
            onClick={handleRevokeApiKey}
          >
            Révoquer
          </Button>,
        ]}
      >
        <p>
          Êtes-vous sûr de vouloir révoquer votre clé API ? Toutes les intégrations
          utilisant cette clé cesseront de fonctionner immédiatement.
        </p>
      </Modal>
    </PageScaffold>
  );
};

export default CompanySettingsPage;
